/**
 * Deep merge utility function to replace lodash merge
 * Recursively merges properties from source objects into a target object
 * @param target The target object to merge into
 * @param sources Source objects to merge from
 * @returns Merged object
 */
export const deepMerge = <T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (source === undefined) return target;

  Object.keys(source).forEach((key: string) => {
    const targetValue = (target as any)[key];
    const sourceValue = (source as any)[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      // For arrays, replace with source array
      (target as any)[key] = [...sourceValue];
    } else if (
      targetValue !== null &&
      sourceValue !== null &&
      typeof targetValue === 'object' &&
      typeof sourceValue === 'object' &&
      !Array.isArray(targetValue) &&
      !Array.isArray(sourceValue)
    ) {
      // For objects, recursively merge
      (target as any)[key] = deepMerge({ ...targetValue }, sourceValue);
    } else if (sourceValue !== undefined) {
      // For primitives and null, replace with source value
      (target as any)[key] = sourceValue;
    }
  });

  return deepMerge(target, ...sources);
};
